import React, { useState } from "react";
import classNames from "classnames";
import {
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
  UrbanItem,
  UrbanStageKey,
  POINT_TYPES,
  POINT_STYLE,
  STATUS_STYLE,
  PAY_STYLE,
  URBAN_CITIES,
  COURIERS,
  money,
  kg,
  toFloat,
} from "./urbain-shared";
import UrbainKanban from "./urbain-kanban";
import { UrbainDetailModal } from "./urbain-modals";
import {
  listExpeditionsReceptionnees,
  setQuartierDestination,
} from "../../../services/crm/clementino-service";
import {
  ClementinoExpedition,
  FiltresExpeditions,
  normalizeStatus,
} from "../../../models/crm/clementino/clementino-types";

export const clementinoToUrbanItem = (e: ClementinoExpedition): UrbanItem => {
  const destCity = typeof e.villeDestination === "object" && e.villeDestination !== null
    ? ((e.villeDestination as any).nom || (e.villeDestination as any).name || "")
    : String(e.villeDestination || "");
  const expCity = typeof e.villeExpedition === "object" && e.villeExpedition !== null
    ? ((e.villeExpedition as any).nom || (e.villeExpedition as any).name || "")
    : String(e.villeExpedition || "");
  const city = destCity || expCity || "Douala";

  const colisList: any[] = Array.isArray(e.colis)
    ? e.colis
    : e.colis && typeof e.colis === "object"
    ? [e.colis]
    : [];

  const numColis = colisList.length > 0 ? colisList.reduce((s, c) => s + toFloat(c.nombre, 1), 0) : toFloat((e as any).nbTotalColis, 1);
  const poids = colisList.length > 0 ? colisList.reduce((s, c) => s + toFloat(c.poidsColis ?? c.poids, 0), 0) : 1.5;
  const nature = colisList[0] ? (colisList[0].description || String(colisList[0].natureColis || "Colis urbain")) : String((e as any).natureColis || "Colis urbain");

  const coursierNom = e.coursier ? `${e.coursier.nom} ${e.coursier.prenoms || ""}`.trim() : "";
  const pickupQuartier = e.quartierExpeditionNom || "Agence Akwa";
  const dropQuartier = e.quartierDestinationNom || (e.quartierDestination ? String(e.quartierDestination) : "A préciser");

  const rawStatus = (e as any).status ?? (e as any).statut;
  const stUpper = normalizeStatus(rawStatus).toUpperCase();
  const isLivre = stUpper === "LIVRE" || stUpper.includes("LIVR");
  const isEnLivraison = stUpper === "EN_LIVRAISON" || !!e.coursier;
  const statut = isLivre ? "Livré" : isEnLivraison ? "En livraison" : "En dépôt";

  const montant = toFloat(e.montant, 2500);
  const cod = toFloat(e.montantARecupererAuClientFinal, 0);
  const paid = e.modePaiement ? montant : 0;

  return {
    id: String(e.id),
    numericId: e.id,
    code: e.numLt || (e as any).numLettreTransport || (e as any).numExpedition || (e as any).numeroExpedition || `LIV-${e.id}`,
    createdBy: "Agent Clementino",
    createdAt: e.dateCreation ? new Date(e.dateCreation).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—",
    source: "Agence",
    service: "Livraison",
    city,
    sender: e.contactExpediteur || "Expéditeur inconnu",
    senderPhone: e.telExpediteur || "—",
    senderKind: "Particulier",
    receiver: e.contactDestinataire || "Destinataire inconnu",
    receiverPhone: e.telDestinataire || "—",
    pickupType: "Agence",
    pickupPlace: e.pointDeCollecte || "Agence dépôt",
    pickupQuartier,
    dropType: "Domicile",
    dropPlace: e.adresseDestination || dropQuartier,
    dropQuartier,
    date: e.dateLivraisonSouhaite ? e.dateLivraisonSouhaite.slice(0, 10) : (e.dateCreation ? e.dateCreation.slice(0, 10) : ""),
    creneau: e.heureLivraisonSouhaite || "09:00–12:00",
    colis: numColis,
    poids,
    nature,
    valeur: toFloat(e.montant, 0),
    cod,
    codCollected: false,
    montant,
    paid,
    pay: (e.modePaiement ? "Payé" : "Non payé") as UrbanItem["pay"],
    refOM: e.numeroTelPaiement || "—",
    refTouch: "—",
    canal: e.modePaiement || "Espèces",
    confirmed: !!e.modePaiement,
    coursier: coursierNom,
    vehicule: "Moto",
    statut,
    statutInfo: e.validePourLivraison ? "Validé pour livraison" : "En attente de validation livraison",
    codeRetrait: "",
    tentatives: 0,
    audit: `Réceptionné agence #${e.id}`,
    note: e.instructionsLivraison || "",
    validePourLivraison: e.validePourLivraison ?? false,
    quartierDestinationId: typeof e.quartierDestination === "number" ? e.quartierDestination : null,
    quartierDestinationNom: dropQuartier,
  };
};

export const URBAN_SEED: UrbanItem[] = [];

/* -------------------------------------------------------------------------- */
/*                                Table columns                               */
/* -------------------------------------------------------------------------- */

const amountLabel = (r: UrbanItem) => {
  if (r.pay === "Remboursé") return "Remboursé";
  if (toFloat(r.cod) > 0 && !r.codCollected) return "CR à encaisser";
  return toFloat(r.montant) - toFloat(r.paid) > 0 ? "Frais à encaisser" : "Soldé";
};

type ColumnDef = {
  key: string;
  label: string;
  width: number;
  align?: "left" | "right" | "center";
  sortValue?: (r: UrbanItem) => string | number;
  searchText?: (r: UrbanItem) => string;
  filterValue?: (r: UrbanItem) => string;
};

const TABLE_COLUMNS: ColumnDef[] = [
  { key: "code", label: "Code / Date", width: 190, sortValue: (r) => r.code, searchText: (r) => `${r.code} ${r.createdBy} ${r.createdAt}` },
  { key: "service", label: "Service", width: 160, sortValue: (r) => r.service, filterValue: (r) => r.service },
  { key: "parties", label: "Expéditeur & Destinataire", width: 250, sortValue: (r) => r.sender.toLowerCase(), searchText: (r) => `${r.sender} ${r.senderPhone} ${r.receiver} ${r.receiverPhone} ${r.senderKind}` },
  { key: "collecte", label: "Collecte", width: 230, sortValue: (r) => r.pickupType, filterValue: (r) => r.pickupType },
  { key: "remise", label: "Remise", width: 240, sortValue: (r) => r.dropType, filterValue: (r) => r.dropType },
  { key: "ville", label: "Ville & créneau", width: 170, sortValue: (r) => `${r.date} ${r.creneau}`, filterValue: (r) => r.city },
  { key: "coursier", label: "Coursier", width: 170, sortValue: (r) => r.coursier || "~", searchText: (r) => `${r.coursier || "non affecté"} ${r.vehicule}` },
  { key: "colis", label: "Colis & Poids", width: 160, sortValue: (r) => r.poids, searchText: (r) => `${r.colis} colis ${r.nature}` },
  { key: "paiement", label: "Paiement & Réf", width: 190, sortValue: (r) => r.pay, filterValue: (r) => r.pay },
  { key: "montant", label: "Frais & CR", width: 160, align: "right", sortValue: (r) => r.montant, filterValue: amountLabel },
  { key: "validation", label: "Validation livraison", width: 160, sortValue: (r) => (r.validePourLivraison ? "Oui" : "Non"), filterValue: (r) => (r.validePourLivraison ? "Validé" : "Non validé") },
  { key: "statut", label: "Statut", width: 210, sortValue: (r) => r.statut, filterValue: (r) => r.statut },
  { key: "actions", label: "Actions", width: 80, align: "center" },
];

const HIDDEN_COLS_STORAGE_KEY = "urbain.hiddenColumns";
const COL_WIDTHS_STORAGE_KEY = "urbain.columnWidths";
const MIN_COL_WIDTH = 70;

function loadStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveStored(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

const isOngoing = (r: UrbanItem) => ["Collecte planifiée", "Collecté", "En dépôt", "En livraison"].includes(r.statut);

function PointChip({ type }: { type: UrbanItem["pickupType"] }) {
  return (
    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: POINT_STYLE[type].bg, color: POINT_STYLE[type].fg }}>
      {type}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                                    Page                                    */
/* -------------------------------------------------------------------------- */

export default function UrbainPage({
  data,
  setData,
  onOpenWizard,
}: {
  data: UrbanItem[];
  setData: React.Dispatch<React.SetStateAction<UrbanItem[]>>;
  onOpenWizard?: () => void;
}) {
  const [view, setView] = useState<"table" | "kanban" | "grid">("table");
  const [q, setQ] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [showKpiCards, setShowKpiCards] = useState(true);
  const [isFullScreenPage, setIsFullScreenPage] = useState(false);
  const [isFullScreenTable, setIsFullScreenTable] = useState(false);
  const [detail, setDetail] = useState<UrbanItem | null>(null);
  const [fCity, setFCity] = useState("Toutes");
  const [fService, setFService] = useState("Tous");
  const [fDrop, setFDrop] = useState("Tous");
  const [fStatut, setFStatut] = useState("Tous");
  const [fPay, setFPay] = useState("Tous");
  const [fCourier, setFCourier] = useState("Tous");
  const [fValidation, setFValidation] = useState("Tous");
  const [fDateDebut, setFDateDebut] = useState("");
  const [fDateFin, setFDateFin] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal valider livraison & quartier
  const [validatingItem, setValidatingItem] = useState<UrbanItem | null>(null);
  const [targetQuartier, setTargetQuartier] = useState("");
  const [targetValide, setTargetValide] = useState(true);
  const [submittingValidation, setSubmittingValidation] = useState(false);

  const fetchUrbanData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filtres: FiltresExpeditions = {};
      if (fDateDebut) filtres.date_debut = fDateDebut;
      if (fDateFin) filtres.date_fin = fDateFin;
      if (q.trim()) filtres.search = q.trim();

      const entities = await listExpeditionsReceptionnees(filtres);
      const items = entities.map(clementinoToUrbanItem);
      setData(items);
    } catch (err: any) {
      setError(
        err?.message ||
        "Impossible de charger les expéditions réceptionnées depuis Clementino. Vérifiez votre clé Gateway API."
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [fDateDebut, fDateFin, q, setData]);

  React.useEffect(() => {
    fetchUrbanData();
  }, [fetchUrbanData]);

  const handleValidateDelivery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatingItem) return;
    setSubmittingValidation(true);
    try {
      const qId = targetValide ? (parseInt(targetQuartier) || 1) : null;
      await setQuartierDestination(
        validatingItem.numericId || validatingItem.id,
        qId,
        targetValide,
        validatingItem.code
      );
      setToast(`Expédition ${validatingItem.code} validée pour livraison sur Clementino`);
      setTimeout(() => setToast(""), 3000);
      setData((prev) =>
        prev.map((r) =>
          r.id === validatingItem.id
            ? {
                ...r,
                validePourLivraison: targetValide,
                quartierDestinationNom: targetQuartier || r.dropQuartier,
                statutInfo: targetValide ? "Validé pour livraison" : "Non validé pour livraison",
              }
            : r
        )
      );
      setValidatingItem(null);
    } catch (err: any) {
      setToast("Erreur lors de la validation: " + (err?.message || "Erreur serveur"));
      setTimeout(() => setToast(""), 3500);
    } finally {
      setSubmittingValidation(false);
    }
  };
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [hiddenCols, setHiddenCols] = useState<string[]>(() => loadStored(HIDDEN_COLS_STORAGE_KEY, []));
  const [colsMenuOpen, setColsMenuOpen] = useState(false);
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => loadStored(COL_WIDTHS_STORAGE_KEY, {}));
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(null);

  const show = (key: string) => !hiddenCols.includes(key);
  const toggleCol = (key: string) => {
    const next = show(key) ? [...hiddenCols, key] : hiddenCols.filter((k) => k !== key);
    setHiddenCols(next);
    saveStored(HIDDEN_COLS_STORAGE_KEY, next);
  };
  const visibleCols = TABLE_COLUMNS.filter((c) => show(c.key));
  const colWidth = (c: ColumnDef) => colWidths[c.key] ?? c.width;
  const activeColFilters = Object.values(colFilters).filter((v) => v.trim()).length;

  const toggleSort = (key: string) => {
    if (sort?.key !== key) setSort({ key, dir: "asc" });
    else if (sort.dir === "asc") setSort({ key, dir: "desc" });
    else setSort(null);
  };

  const startResize = (e: React.MouseEvent, col: ColumnDef) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidth(col);
    let latest = colWidths;
    const onMove = (ev: MouseEvent) => {
      latest = { ...latest, [col.key]: Math.max(MIN_COL_WIDTH, Math.round(startW + ev.clientX - startX)) };
      setColWidths(latest);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      saveStored(COL_WIDTHS_STORAGE_KEY, latest);
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const resetColWidth = (col: ColumnDef) => {
    const next = { ...colWidths };
    delete next[col.key];
    setColWidths(next);
    saveStored(COL_WIDTHS_STORAGE_KEY, next);
  };

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2400);
  };

  const filtered = data.filter((r) => {
    if (fCity !== "Toutes" && r.city !== fCity) return false;
    if (fService !== "Tous" && r.service !== fService) return false;
    if (fDrop !== "Tous" && r.dropType !== fDrop) return false;
    if (fStatut !== "Tous" && r.statut !== fStatut) return false;
    if (fPay !== "Tous" && r.pay !== fPay) return false;
    if (fCourier !== "Tous" && r.coursier !== fCourier) return false;
    if (fValidation === "Validé" && !r.validePourLivraison) return false;
    if (fValidation === "Non validé" && r.validePourLivraison) return false;
    if (q.trim()) {
      const hay = [r.code, r.sender, r.receiver, r.senderPhone, r.receiverPhone, r.refOM, r.refTouch, r.city, r.pickupPlace, r.dropPlace, r.pickupQuartier, r.dropQuartier, r.coursier, r.codeRetrait]
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q.trim().toLowerCase())) return false;
    }
    for (const col of TABLE_COLUMNS) {
      const f = colFilters[col.key]?.trim();
      if (!f) continue;
      if (col.filterValue && col.filterValue(r) !== f) return false;
      if (col.searchText && !col.searchText(r).toLowerCase().includes(f.toLowerCase())) return false;
    }
    return true;
  });

  const sortCol = sort && TABLE_COLUMNS.find((c) => c.key === sort.key);
  if (sort && sortCol?.sortValue) {
    const getVal = sortCol.sortValue;
    const factor = sort.dir === "asc" ? 1 : -1;
    filtered.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb), "fr");
      return cmp * factor;
    });
  }

  const filterOptions = (col: ColumnDef) =>
    col.filterValue ? Array.from(new Set(data.map(col.filterValue))).sort((a, b) => a.localeCompare(b, "fr")) : [];

  const totalKg = filtered.reduce((a, r) => a + toFloat(r.poids), 0);
  const totalPaid = filtered.reduce((a, r) => a + (r.pay === "Remboursé" ? 0 : toFloat(r.paid)), 0);
  const codDue = (rows: UrbanItem[]) => rows.filter((r) => toFloat(r.cod) > 0 && !r.codCollected && r.statut !== "Annulé").reduce((a, r) => a + toFloat(r.cod), 0);
  const selCount = Object.values(sel).filter(Boolean).length;

  const handleConfirmPay = (id: string, code: string) => {
    setData((prev) => prev.map((r) => (r.id === id ? { ...r, confirmed: true, pay: "Payé", paid: r.montant } : r)));
    flash(`Paiement confirmé — ${code}`);
    setOpenMenuId(null);
  };

  const handleStageMove = (id: string, stage: UrbanStageKey, patch: Partial<UrbanItem>, note: string) => {
    const stamp = new Date().toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    setData((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch, stage, audit: `${r.audit} · ${stamp} ${note}` } : r)));
    flash(`${data.find((r) => r.id === id)?.code ?? "Livraison"} — ${note}`);
  };

  const selectCls = "w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white";

  return (
    <div
      className={classNames(
        "h-full flex flex-col bg-[#faf7ff] text-[#1f1b2e] transition-all",
        // overflow-clip (not hidden) so the sticky header below keeps working
        isFullScreenPage ? "fixed inset-0 z-50 p-6 bg-[#faf7ff] overflow-hidden" : "p-6 lg:p-7 overflow-clip"
      )}
    >
      {/* Header bar — stays pinned under the app header while the page scrolls */}
      <div
        className={classNames(
          "flex items-start justify-between gap-x-6 gap-y-3 flex-wrap mb-4 shrink-0",
          !isFullScreenPage && "sticky top-[50px] z-30 -mx-6 px-6 lg:-mx-7 lg:px-7 -mt-6 lg:-mt-7 pt-6 lg:pt-7 pb-3 bg-[#faf7ff]"
        )}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold tracking-tight text-[#1f1b2e]">Livraison Urbaine (Collecte, Points relais & Lockers)</h1>
            <button
              type="button"
              onClick={() => setIsFullScreenPage(!isFullScreenPage)}
              title={isFullScreenPage ? "Quitter le plein écran de la page" : "Plein écran sur toute la page"}
              className="p-1.5 rounded-lg border border-blue-200 bg-white hover:bg-blue-50 text-blue-800 text-xs font-semibold flex items-center gap-1 shrink-0 whitespace-nowrap shadow-2xs cursor-pointer"
            >
              {isFullScreenPage ? <ArrowsPointingInIcon className="w-3.5 h-3.5" /> : <ArrowsPointingOutIcon className="w-3.5 h-3.5" />}
              <span>{isFullScreenPage ? "Réduire" : "Plein écran"}</span>
            </button>
            <button
              type="button"
              onClick={() => setShowKpiCards(!showKpiCards)}
              title={showKpiCards ? "Masquer les statistiques" : "Afficher les statistiques"}
              className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-semibold flex items-center gap-1 shrink-0 whitespace-nowrap shadow-2xs cursor-pointer"
            >
              {showKpiCards ? <EyeSlashIcon className="w-3.5 h-3.5" /> : <EyeIcon className="w-3.5 h-3.5" />}
              <span>{showKpiCards ? "Masquer stats" : "Afficher stats"}</span>
            </button>
          </div>
          <p className="text-xs text-[#6b6480] mt-1 font-medium">
            {filtered.length} livraisons sur {data.length} · {data.filter(isOngoing).length} en cours ·{" "}
            {data.filter((r) => r.statut === "Disponible au point").length} en attente de retrait ·{" "}
            {data.filter((r) => r.statut === "Échec de livraison" || r.statut === "Retour expéditeur").length} échec(s)
          </p>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            type="button"
            onClick={fetchUrbanData}
            disabled={loading}
            className="flex items-center gap-1.5 h-9 px-3.5 border border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-900 rounded-lg text-xs font-semibold cursor-pointer shadow-xs disabled:opacity-50"
          >
            <ArrowPathIcon className={classNames("w-3.5 h-3.5", loading && "animate-spin")} />
            <span>Actualiser Clementino</span>
          </button>
          <button
            type="button"
            onClick={() => flash(`Export Excel généré (${filtered.length} lignes)`)}
            className="flex items-center gap-1.5 h-9 px-3.5 border border-slate-300 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold text-slate-700 shadow-xs cursor-pointer"
          >
            <span className="w-2 h-2 rounded-xs bg-emerald-600" />
            Export Excel
          </button>
          <button
            type="button"
            onClick={() => flash(`Feuille de route générée pour ${data.filter(isOngoing).length} course(s)`)}
            className="flex items-center gap-1.5 h-9 px-3.5 border border-slate-300 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold text-slate-700 shadow-xs cursor-pointer"
          >
            <span className="w-2 h-2 rounded-xs bg-rose-600" />
            Feuille de route
          </button>
          <button
            type="button"
            onClick={() => flash("Positions GPS des coursiers actualisées")}
            className="h-9 px-3.5 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-lg text-xs font-semibold text-blue-700 shadow-xs cursor-pointer"
          >
            Suivi coursiers
          </button>
        </div>
      </div>

      {/* Error alert banner */}
      {error && (
        <div className="mb-3.5 p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 flex items-start justify-between gap-3 animate-fade-in shrink-0">
          <div className="flex items-start gap-2.5">
            <ExclamationTriangleIcon className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-xs font-bold">Erreur de chargement Clementino</div>
              <div className="text-xs mt-0.5 text-rose-700">{error}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={fetchUrbanData}
            className="px-2.5 py-1 bg-white border border-rose-300 rounded text-xs font-semibold text-rose-700 hover:bg-rose-100 shrink-0 cursor-pointer"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* KPI Cards */}
      {showKpiCards && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3.5 shrink-0 animate-fade-in">
          <Kpi title="Demandes / devis" value={data.filter((r) => r.pay === "Non payé" && r.cod === 0 && r.statut === "Demande reçue").length} sub="non payées" color="text-rose-700" />
          <Kpi title="À planifier" value={data.filter((r) => r.statut === "Demande reçue" && (r.pay !== "Non payé" || r.cod > 0)).length} sub="collecte / tournée à affecter" color="text-amber-700" />
          <Kpi title="En cours" value={data.filter(isOngoing).length} sub="collecte, dépôt, livraison" color="text-sky-700" />
          <Kpi title="Aux points de retrait" value={data.filter((r) => r.statut === "Disponible au point").length} sub="relais, lockers, supermarchés" color="text-teal-700" />
          <Kpi title="CR à encaisser" value={money(codDue(data))} sub="contre-remboursements" color="text-purple-700" />
        </div>
      )}

      {/* View switcher & search */}
      <div className="flex items-center gap-3 flex-wrap mb-3 shrink-0">
        <div className="flex p-0.5 bg-[#e0e7ff] rounded-lg gap-1">
          {(
            [
              ["table", "Tableau"],
              ["kanban", "Kanban statut"],
              ["grid", "Grille"],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={classNames(
                "px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer",
                view === v ? "bg-white text-blue-900 shadow-xs" : "text-[#64748b] hover:text-blue-900"
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Code, expéditeur, destinataire, tél, quartier, point, coursier, code de retrait..."
          className="flex-1 min-w-[260px] h-9 px-3.5 border border-blue-200/90 rounded-lg bg-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <button
          type="button"
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="h-9 px-3.5 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-lg text-xs font-semibold text-blue-900 cursor-pointer"
        >
          {filtersOpen ? "Masquer les filtres" : "Filtres"}
        </button>
      </div>

      {/* Filter panel */}
      {filtersOpen && (
        <div className="p-3.5 bg-white border border-[#e0e7ff] rounded-xl mb-3 shrink-0 shadow-xs">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
            <FilterSelect label="Ville" value={fCity} onChange={setFCity} all="Toutes" allLabel="Toutes les villes" options={URBAN_CITIES} cls={selectCls} />
            <FilterSelect label="Service" value={fService} onChange={setFService} all="Tous" allLabel="Tous les services" options={["Livraison", "Collecte", "Collecte + livraison"]} cls={selectCls} />
            <FilterSelect label="Lieu de remise" value={fDrop} onChange={setFDrop} all="Tous" allLabel="Tous les lieux" options={POINT_TYPES} cls={selectCls} />
            <FilterSelect label="Statut" value={fStatut} onChange={setFStatut} all="Tous" allLabel="Tous statuts" options={Object.keys(STATUS_STYLE)} cls={selectCls} />
            <FilterSelect label="Paiement" value={fPay} onChange={setFPay} all="Tous" allLabel="Tous paiements" options={["Payé", "Non payé", "Partiel", "Remboursé"]} cls={selectCls} />
            <FilterSelect label="Coursier" value={fCourier} onChange={setFCourier} all="Tous" allLabel="Tous coursiers" options={COURIERS} cls={selectCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-2.5 pt-2.5 border-t border-gray-100">
            <div>
              <span className="block text-[10px] font-bold text-[#7b86a8] uppercase mb-1">Validation livraison</span>
              <select value={fValidation} onChange={(e) => setFValidation(e.target.value)} className={selectCls}>
                <option value="Tous">Toutes les livraisons</option>
                <option value="Validé">Validé pour livraison</option>
                <option value="Non validé">Non validé (retrait agence)</option>
              </select>
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#7b86a8] uppercase mb-1">Date début</span>
              <input
                type="date"
                value={fDateDebut}
                onChange={(e) => setFDateDebut(e.target.value)}
                className={selectCls}
              />
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#7b86a8] uppercase mb-1">Date fin</span>
              <input
                type="date"
                value={fDateFin}
                onChange={(e) => setFDateFin(e.target.value)}
                className={selectCls}
              />
            </div>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selCount > 0 && (
        <div className="flex items-center gap-3 px-4 py-2 bg-[#1f1b2e] rounded-xl text-white text-xs mb-3 shrink-0 flex-wrap">
          <span className="font-bold">{selCount} livraison(s) sélectionnée(s)</span>
          <div className="h-4 w-px bg-gray-600" />
          <button type="button" onClick={() => flash(`Tournée créée pour ${selCount} livraison(s)`)} className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 rounded font-semibold cursor-pointer">
            Créer une tournée
          </button>
          <button type="button" onClick={() => flash(`Étiquettes imprimées pour ${selCount} livraison(s)`)} className="px-2.5 py-1 bg-gray-700 hover:bg-gray-600 rounded font-semibold cursor-pointer">
            Imprimer étiquettes
          </button>
          <button type="button" onClick={() => flash(`Codes de retrait renvoyés (${selCount})`)} className="px-2.5 py-1 bg-gray-700 hover:bg-gray-600 rounded font-semibold cursor-pointer">
            Renvoyer les codes
          </button>
          <button type="button" onClick={() => setSel({})} className="ml-auto text-gray-400 hover:text-white cursor-pointer">
            Désélectionner
          </button>
        </div>
      )}

      {/* Content */}
      <div
        className={classNames(
          "flex-1 min-h-0 overflow-hidden flex flex-col bg-white border border-[#e0e7ff] rounded-xl shadow-xs transition-all",
          isFullScreenTable ? "fixed inset-0 z-50 p-6 bg-[#faf7ff]" : ""
        )}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#f8faff] border-b border-[#e0e7ff] text-xs whitespace-nowrap">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-bold text-[#1f1b2e]">Liste des livraisons</span>
            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full font-bold text-[11px]">{filtered.length} lignes</span>
          </div>
          <div className="flex items-center gap-2">
            {view === "table" && (activeColFilters > 0 || sort) && (
              <button
                type="button"
                onClick={() => {
                  setColFilters({});
                  setSort(null);
                }}
                className="px-2.5 py-1 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-md border border-rose-200 cursor-pointer"
              >
                Effacer filtres & tri{activeColFilters > 0 ? ` (${activeColFilters})` : ""}
              </button>
            )}
            {view === "table" && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setColsMenuOpen(!colsMenuOpen)}
                  title="Afficher / masquer des colonnes"
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 rounded-md border border-gray-200 cursor-pointer"
                >
                  <EyeIcon className="w-3.5 h-3.5" />
                  <span>Colonnes</span>
                  {hiddenCols.length > 0 && (
                    <span className="px-1.5 bg-blue-100 text-blue-800 rounded-full text-[10px] font-bold">
                      {TABLE_COLUMNS.length - hiddenCols.length}/{TABLE_COLUMNS.length}
                    </span>
                  )}
                </button>
                {colsMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setColsMenuOpen(false)} />
                    <div className="absolute right-0 top-8 z-40 w-60 bg-white border border-gray-200 rounded-xl shadow-xl p-1.5">
                      {TABLE_COLUMNS.map((col) => (
                        <label key={col.key} className="flex items-center gap-2 px-2.5 py-1.5 rounded hover:bg-blue-50 text-xs text-gray-700 cursor-pointer">
                          <input type="checkbox" checked={show(col.key)} onChange={() => toggleCol(col.key)} className="w-3.5 h-3.5 accent-blue-700" />
                          {col.label}
                        </label>
                      ))}
                      {hiddenCols.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setHiddenCols([]);
                            saveStored(HIDDEN_COLS_STORAGE_KEY, []);
                          }}
                          className="w-full mt-1 px-2.5 py-1.5 border-t border-gray-100 text-left text-xs font-semibold text-blue-700 hover:bg-blue-50 rounded"
                        >
                          Tout afficher
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {onOpenWizard && (
              <button type="button" onClick={onOpenWizard} className="px-2.5 py-1 bg-[#2563eb] hover:bg-blue-700 text-white rounded-md text-xs font-bold shadow-2xs">
                + Nouvelle livraison
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsFullScreenTable(!isFullScreenTable)}
              title={isFullScreenTable ? "Quitter le plein écran du tableau" : "Zoom plein écran sur le tableau uniquement"}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200 cursor-pointer"
            >
              {isFullScreenTable ? <ArrowsPointingInIcon className="w-3.5 h-3.5" /> : <ArrowsPointingOutIcon className="w-3.5 h-3.5" />}
              <span>{isFullScreenTable ? "Réduire tableau" : "Plein écran"}</span>
            </button>
          </div>
        </div>

        {view === "table" && (
          <div className="flex-1 overflow-auto tracking-scrollbar">
            <table
              className="table-fixed min-w-full border-separate border-spacing-0 text-xs"
              style={{ width: 40 + visibleCols.reduce((sum, c) => sum + colWidth(c), 0) }}
            >
              <colgroup>
                <col style={{ width: 40 }} />
                {visibleCols.map((c) => (
                  <col key={c.key} style={{ width: colWidth(c) }} />
                ))}
              </colgroup>
              <thead className="sticky top-0 bg-[#f5f7fd] z-20 shadow-2xs">
                <tr className="text-left text-[11px] font-bold text-gray-600 tracking-wider uppercase whitespace-nowrap">
                  <th className="p-3 sticky left-0 z-10 bg-[#f5f7fd]">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && filtered.every((r) => sel[r.id])}
                      onChange={(e) => {
                        const next: Record<string, boolean> = {};
                        filtered.forEach((r) => (next[r.id] = e.target.checked));
                        setSel(next);
                      }}
                      className="w-3.5 h-3.5 accent-blue-700"
                    />
                  </th>
                  {visibleCols.map((col) => {
                    const sorted = sort?.key === col.key ? sort.dir : null;
                    return (
                      <th
                        key={col.key}
                        className={classNames("p-0 bg-[#f5f7fd]", col.key === "code" ? "sticky left-10 z-10 shadow-[inset_-1px_0_0_#e0e7ff]" : "relative")}
                        aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : undefined}
                      >
                        {col.sortValue ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(col.key)}
                            title="Trier"
                            className={classNames(
                              "w-full flex items-center gap-1 p-3 pr-4 uppercase tracking-wider cursor-pointer hover:text-blue-800",
                              col.align === "right" && "justify-end",
                              sorted && "text-blue-800"
                            )}
                          >
                            <span className="truncate">{col.label}</span>
                            {sorted === "asc" ? (
                              <ChevronUpIcon className="w-3 h-3 shrink-0" />
                            ) : sorted === "desc" ? (
                              <ChevronDownIcon className="w-3 h-3 shrink-0" />
                            ) : (
                              <ChevronUpDownIcon className="w-3 h-3 shrink-0 text-gray-300" />
                            )}
                          </button>
                        ) : (
                          <div className={classNames("p-3 truncate", col.align === "center" && "text-center")}>{col.label}</div>
                        )}
                        <div
                          onMouseDown={(e) => startResize(e, col)}
                          onDoubleClick={() => resetColWidth(col)}
                          title="Glisser pour redimensionner · double-clic pour réinitialiser"
                          className="absolute top-0 right-0 h-full w-2 cursor-col-resize group/resize flex justify-center"
                        >
                          <div className="w-px h-full bg-blue-100 group-hover/resize:bg-blue-400 group-hover/resize:w-0.5" />
                        </div>
                      </th>
                    );
                  })}
                </tr>
                <tr className="[&>th]:border-b [&>th]:border-blue-100 [&>th]:bg-[#f5f7fd]">
                  <th className="px-3 pb-2 sticky left-0 z-10" />
                  {visibleCols.map((col) => (
                    <th key={col.key} className={classNames("px-2 pb-2 font-normal", col.key === "code" && "sticky left-10 z-10 shadow-[inset_-1px_0_0_#e0e7ff]")}>
                      {col.filterValue ? (
                        <select
                          value={colFilters[col.key] ?? ""}
                          onChange={(e) => setColFilters({ ...colFilters, [col.key]: e.target.value })}
                          className={classNames(
                            "w-full h-7 px-1.5 rounded-md border text-[11px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-300",
                            colFilters[col.key] ? "border-blue-400 text-blue-900 font-semibold" : "border-gray-200 text-gray-500"
                          )}
                        >
                          <option value="">Tous</option>
                          {filterOptions(col).map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      ) : col.searchText ? (
                        <div className="relative">
                          <MagnifyingGlassIcon className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                          <input
                            type="text"
                            value={colFilters[col.key] ?? ""}
                            onChange={(e) => setColFilters({ ...colFilters, [col.key]: e.target.value })}
                            placeholder="Rechercher…"
                            className={classNames(
                              "w-full h-7 pl-6 pr-1.5 rounded-md border text-[11px] bg-white font-normal normal-case focus:outline-none focus:ring-2 focus:ring-blue-300",
                              colFilters[col.key] ? "border-blue-400 text-blue-900" : "border-gray-200"
                            )}
                          />
                        </div>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const S = STATUS_STYLE[r.statut];
                  const P = PAY_STYLE[r.pay];
                  const due = r.montant - r.paid;
                  const stickyBg = sel[r.id] ? "bg-[#f7f9ff]" : "bg-white group-hover:bg-[#f8faff]";
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setDetail(r)}
                      className={classNames(
                        "group align-top hover:bg-[#f8faff] transition-colors cursor-pointer [&>td]:border-b [&>td]:border-gray-100 [&>td]:overflow-hidden [&>td]:text-ellipsis",
                        sel[r.id] ? "bg-[#f7f9ff]" : ""
                      )}
                    >
                      <td className={classNames("p-3 sticky left-0 z-[5]", stickyBg)} onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={!!sel[r.id]} onChange={(e) => setSel({ ...sel, [r.id]: e.target.checked })} className="w-3.5 h-3.5 accent-blue-700" />
                      </td>
                      {show("code") && (
                        <td className={classNames("p-3 whitespace-nowrap sticky left-10 z-[5] shadow-[inset_-1px_0_0_#e0e7ff]", stickyBg)}>
                          <span className="font-mono font-semibold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded">{r.code}</span>
                          <div className="text-[11px] text-gray-500 mt-1">
                            {r.createdBy} · {r.createdAt.split(" · ")[0]}
                          </div>
                        </td>
                      )}
                      {show("service") && (
                        <td className="p-3 whitespace-nowrap">
                          <div className="font-semibold text-gray-900">{r.service}</div>
                          <div className="text-[11px] text-gray-500">{r.source}</div>
                        </td>
                      )}
                      {show("parties") && (
                        <td className="p-3">
                          <div className="font-semibold text-gray-900 whitespace-nowrap">
                            {r.sender} <span className="text-[10px] font-normal text-gray-400">· {r.senderKind}</span>
                          </div>
                          <div className="font-mono text-[11px] text-gray-500 whitespace-nowrap">{r.senderPhone}</div>
                          <div className="text-[11px] text-gray-400 mt-1">
                            Dest: <span className="text-gray-700 font-medium">{r.receiver}</span> <span className="whitespace-nowrap">({r.receiverPhone})</span>
                          </div>
                        </td>
                      )}
                      {show("collecte") && (
                        <td className="p-3 whitespace-nowrap">
                          <PointChip type={r.pickupType} />
                          <div className="font-medium text-gray-900 mt-1 truncate" title={r.pickupPlace}>
                            {r.pickupPlace}
                          </div>
                          <div className="text-[11px] text-gray-500">{r.pickupQuartier}</div>
                        </td>
                      )}
                      {show("remise") && (
                        <td className="p-3 whitespace-nowrap">
                          <PointChip type={r.dropType} />
                          <div className="font-medium text-gray-900 mt-1 truncate" title={r.dropPlace}>
                            {r.dropPlace}
                          </div>
                          <div className="text-[11px] text-gray-500">
                            {r.dropQuartier}
                            {r.codeRetrait && <span className="ml-1 font-mono text-teal-700">· code {r.codeRetrait}</span>}
                          </div>
                        </td>
                      )}
                      {show("ville") && (
                        <td className="p-3 whitespace-nowrap">
                          <div className="font-semibold text-gray-900">{r.city}</div>
                          <div className="text-[11px] text-gray-500">{r.date.split("-").reverse().join("/")}</div>
                          <div className="text-[11px] text-blue-700 font-semibold">{r.creneau}</div>
                        </td>
                      )}
                      {show("coursier") && (
                        <td className="p-3 whitespace-nowrap">
                          {r.coursier ? (
                            <>
                              <div className="font-medium text-gray-900">{r.coursier}</div>
                              <div className="text-[11px] text-gray-500">{r.vehicule}</div>
                            </>
                          ) : (
                            <span className="text-[11px] text-gray-400">Non affecté</span>
                          )}
                        </td>
                      )}
                      {show("colis") && (
                        <td className="p-3 whitespace-nowrap">
                          <div className="font-bold text-gray-900">{kg(r.poids)}</div>
                          <div className="text-[11px] text-gray-500">
                            {r.colis} colis · {r.nature}
                          </div>
                        </td>
                      )}
                      {show("paiement") && (
                        <td className="p-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <span className="inline-block px-2 py-0.5 rounded text-[11px] font-bold" style={{ backgroundColor: P.bg, color: P.fg }}>
                            {r.pay}
                          </span>
                          {!r.confirmed && (r.pay === "Payé" || r.pay === "Partiel") && (
                            <button
                              type="button"
                              onClick={() => handleConfirmPay(r.id, r.code)}
                              className="ml-1.5 px-1.5 py-0.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-bold rounded text-[10px] cursor-pointer"
                            >
                              Valider
                            </button>
                          )}
                          <div className="font-mono text-[10px] text-gray-500 mt-1">
                            {r.refOM !== "—" ? `OM: ${r.refOM}` : r.refTouch !== "—" ? `ITX: ${r.refTouch}` : r.cod > 0 && r.pay === "Non payé" ? "Frais au CR" : ""}
                          </div>
                        </td>
                      )}
                      {show("montant") && (
                        <td className="p-3 text-right whitespace-nowrap">
                          <div className="font-bold text-gray-900">{money(r.montant)}</div>
                          {r.cod > 0 && (
                            <div className={classNames("text-[10px] font-semibold", r.codCollected ? "text-emerald-700" : "text-purple-700")}>
                              CR {money(r.cod)} {r.codCollected ? " (Encaissé)" : ""}
                            </div>
                          )}
                          {due > 0 && r.pay !== "Remboursé" && <div className="text-[10px] text-rose-700 font-semibold">Reste {money(due)}</div>}
                        </td>
                      )}
                      {show("validation") && (
                        <td className="p-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1.5">
                            <span
                              className={classNames(
                                "inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold",
                                r.validePourLivraison ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                              )}
                            >
                              {r.validePourLivraison ? "Validé" : "À valider"}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setValidatingItem(r);
                                setTargetQuartier(r.dropQuartier || "");
                                setTargetValide(true);
                              }}
                              className="text-[10px] text-purple-700 underline font-semibold cursor-pointer ml-1"
                            >
                              Modifier
                            </button>
                          </div>
                        </td>
                      )}
                      {show("statut") && (
                        <td className="p-3 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-bold" style={{ backgroundColor: S.bg, color: S.fg }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: S.dot }} />
                            {r.statut}
                          </span>
                          <div className="text-[10px] text-gray-500 mt-1 truncate" title={r.statutInfo}>
                            {r.statutInfo}
                          </div>
                        </td>
                      )}
                      {show("actions") && (
                        <td className="p-3 text-center relative !overflow-visible" onClick={(e) => e.stopPropagation()}>
                          <button type="button" onClick={() => setOpenMenuId(openMenuId === r.id ? null : r.id)} className="w-7 h-7 rounded border border-gray-200 hover:bg-gray-100 text-gray-600 font-bold">
                            ⋯
                          </button>
                          {openMenuId === r.id && (
                            <div className="absolute right-4 top-10 z-30 w-52 bg-white border border-gray-200 rounded-xl shadow-xl p-1 text-left">
                              <MenuBtn label="Ouvrir détails" strong onClick={() => { setDetail(r); setOpenMenuId(null); }} />
                              {!r.confirmed && r.pay !== "Remboursé" && <MenuBtn label="Valider paiement" onClick={() => handleConfirmPay(r.id, r.code)} />}
                              <MenuBtn label="Imprimer étiquette" onClick={() => { flash(`Étiquette imprimée — ${r.code}`); setOpenMenuId(null); }} />
                              <MenuBtn label="WhatsApp destinataire" onClick={() => { flash(`WhatsApp envoyé à ${r.receiver}`); setOpenMenuId(null); }} />
                              {r.codeRetrait && <MenuBtn label="Renvoyer code de retrait" onClick={() => { flash(`Code ${r.codeRetrait} renvoyé à ${r.receiver}`); setOpenMenuId(null); }} />}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={visibleCols.length + 1} className="p-8 text-center text-xs text-gray-500">
                      Aucune livraison ne correspond aux filtres.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {view === "kanban" && <UrbainKanban items={filtered} onOpen={setDetail} onMove={handleStageMove} />}

        {view === "grid" && (
          <div className="flex-1 overflow-y-auto tracking-scrollbar p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {filtered.map((r) => {
              const S = STATUS_STYLE[r.statut];
              return (
                <div key={r.id} onClick={() => setDetail(r)} className="p-4 bg-white border border-gray-200 rounded-xl shadow-2xs flex flex-col justify-between cursor-pointer hover:border-blue-200">
                  <div>
                    <div className="flex justify-between items-start gap-2">
                      <span className="font-mono font-semibold text-xs text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded">{r.code}</span>
                      <span className="px-2 py-0.5 rounded text-[11px] font-bold" style={{ backgroundColor: S.bg, color: S.fg }}>
                        {r.statut}
                      </span>
                    </div>
                    <div className="text-sm font-bold text-gray-900 mt-3">{r.sender}</div>
                    <div className="text-xs text-gray-500 mt-0.5">→ {r.receiver}</div>
                    <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                      <PointChip type={r.pickupType} />
                      <span className="text-gray-400">→</span>
                      <PointChip type={r.dropType} />
                      <span className="ml-auto text-[11px] font-bold text-gray-700">{r.city}</span>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1 truncate">{r.dropPlace}</div>
                    <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-100 text-xs">
                      <div>
                        <span className="text-[10px] text-gray-400 block uppercase font-bold">Créneau</span>
                        <span>
                          {r.date.split("-").reverse().join("/").slice(0, 5)} · {r.creneau}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-400 block uppercase font-bold">Frais{r.cod > 0 ? " · CR" : ""}</span>
                        <span className="font-bold text-gray-900">
                          {money(r.montant)}
                          {r.cod > 0 ? ` · ${money(r.cod)}` : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => handleConfirmPay(r.id, r.code)}
                      disabled={r.confirmed}
                      className={classNames(
                        "flex-1 h-8 text-xs font-bold rounded-lg",
                        r.confirmed ? "bg-gray-100 text-gray-400 cursor-default" : "bg-emerald-100 hover:bg-emerald-200 text-emerald-800 cursor-pointer"
                      )}
                    >
                      {r.confirmed ? "Paiement validé" : "Valider paiement"}
                    </button>
                    <button type="button" onClick={() => flash(`WhatsApp envoyé à ${r.receiver}`)} className="px-3 h-8 border border-gray-200 bg-white hover:bg-gray-50 text-blue-900 text-xs font-semibold rounded-lg cursor-pointer">
                      Contacter
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-4 flex-wrap px-4 py-2.5 bg-[#f8faff] border-t border-[#e0e7ff] text-xs text-gray-600 shrink-0">
          <div>{filtered.length} livraisons affichées</div>
          <div className="flex items-center gap-4 flex-wrap">
            <span>
              Poids total : <strong className="text-gray-900">{kg(totalKg)}</strong>
            </span>
            <span>
              Frais encaissés : <strong className="text-emerald-700">{money(totalPaid)}</strong>
            </span>
            <span>
              CR à encaisser : <strong className="text-purple-700">{money(codDue(filtered))}</strong>
            </span>
          </div>
        </div>
      </div>

      {toast && (
        <div className="fixed left-1/2 bottom-6 -translate-x-1/2 z-50 px-4 py-2 bg-[#1f1b2e] rounded-lg text-xs font-semibold text-white shadow-xl animate-fade-in">
          {toast}
        </div>
      )}

      <UrbainDetailModal
        item={detail && (data.find((r) => r.id === detail.id) ?? detail)}
        onClose={() => setDetail(null)}
        onConfirmPay={handleConfirmPay}
        onFlash={flash}
      />

      {/* Modal Valider pour livraison Clementino */}
      {validatingItem && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-w-md w-full p-6 animate-fade-in">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
              <h3 className="font-bold text-gray-900 text-sm">
                Valider pour livraison urbaine
              </h3>
              <button
                type="button"
                onClick={() => setValidatingItem(null)}
                className="text-gray-400 hover:text-gray-600 text-sm"
              >
                Annuler
              </button>
            </div>
            <form onSubmit={handleValidateDelivery} className="space-y-4">
              <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
                <div>Expédition : <strong className="text-gray-900">{validatingItem.code}</strong></div>
                <div className="mt-1">Destinataire : <strong className="text-gray-900">{validatingItem.receiver}</strong> ({validatingItem.city})</div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Éligibilité à la livraison
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-xs cursor-pointer text-gray-800">
                    <input
                      type="radio"
                      name="validePourLivraison"
                      checked={targetValide}
                      onChange={() => setTargetValide(true)}
                    />
                    <span>Valide pour livraison</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs cursor-pointer text-gray-800">
                    <input
                      type="radio"
                      name="validePourLivraison"
                      checked={!targetValide}
                      onChange={() => setTargetValide(false)}
                    />
                    <span>Non validé (retrait agence)</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Quartier de destination {targetValide ? "(requis pour livraison)" : "(optionnel si non validé)"}
                </label>
                <input
                  type="text"
                  value={targetQuartier}
                  onChange={(e) => setTargetQuartier(e.target.value)}
                  placeholder="Ex: Akwa, Bonanjo, Bastos..."
                  className="w-full h-9 px-3 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-purple-400 focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setValidatingItem(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submittingValidation}
                  className="px-4 py-2 bg-purple-700 hover:bg-purple-800 text-white rounded-lg text-xs font-bold shadow-sm disabled:opacity-50 cursor-pointer"
                >
                  {submittingValidation ? "Enregistrement..." : "Enregistrer Clementino"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ title, value, sub, color }: { title: string; value: React.ReactNode; sub: string; color: string }) {
  return (
    <div className="p-3 bg-white border border-[#e0e7ff] rounded-xl shadow-xs">
      <div className="text-[10px] font-bold tracking-wider uppercase text-[#7b86a8]">{title}</div>
      <div className={classNames("text-xl font-bold mt-1", color)}>{value}</div>
      <div className="text-[11px] text-[#98a2b8]">{sub}</div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  all,
  allLabel,
  options,
  cls,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  all: string;
  allLabel: string;
  options: string[];
  cls: string;
}) {
  return (
    <div>
      <span className="block text-[10px] font-bold text-[#7b86a8] uppercase mb-1">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={cls}>
        <option value={all}>{allLabel}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function MenuBtn({ label, onClick, strong }: { label: string; onClick: () => void; strong?: boolean }) {
  return (
    <button type="button" onClick={onClick} className={classNames("w-full text-left px-3 py-1.5 hover:bg-blue-50 text-xs rounded", strong ? "font-semibold text-blue-900" : "text-gray-700")}>
      {label}
    </button>
  );
}
